import { ProjectContext } from "context/ProjectProvider";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useState
} from "react";
import { QUERY_GET_PROJECT_SPECS } from "services/graphQL/queries";
import { Button, Divider, Select } from "antd";

// Wraps a selected spec-section value in a class-independent `data-testid`
// hook for UI automation. antd v6 restructured the Select's internal DOM
// (single mode dropped the `ant-select-selection-item` span; multiple mode
// nested it deeper), so selectors keyed on those classes break. Rendered via
// the Select's `labelRender`, which fires in both single and multiple mode.
export const renderSpecSectionValue = (
  label: React.ReactNode,
  value: React.ReactNode
) => <span data-testid="select-spec-section-value">{label ?? value}</span>;

export const featureSpecSectionDropdownRenderer = (
  menu: any,
  onCreateSpecSectionClick: () => void
) => (
  <>
    {menu}
    <Divider className="my-1" style={{ margin: "8px 0" }} />
    <div className="w-full px-1">
      <Button
        className="rounded-none w-full"
        onClick={() => {
          if (onCreateSpecSectionClick) {
            onCreateSpecSectionClick();
          }
        }}
        data-testid="index-button-add-new-spec-section"
      >
        Add New Spec Section
      </Button>
    </div>
  </>
);

const FeatureSpecSectionDropdown = forwardRef(
  function FeatureSpecSectionDropdown(
    props: {
      disabled?: boolean;
      selectedIds?: any;
      onBlur: (ids: string[], option: any) => void;
      popupRender?: (menu: any) => React.ReactElement;
      mode?: "multiple" | "single";
      className?: string;
      open?: boolean;
      setSpecSections?: React.Dispatch<React.SetStateAction<any[]>>;
      placeholder?: React.ReactNode;
    },
    ref: any
  ) {
    const {
      onBlur,
      disabled,
      selectedIds,
      popupRender,
      mode = "multiple",
      className,
      open,
      setSpecSections,
      placeholder
    } = props;

    const { gqlClientForProject, eventLogs } = useContext(ProjectContext);

    const [localSelectedIds, setLocalSelectedIds] = useState<string[]>([]);
    const [localOptions, setLocalOptions] = useState<any>();

    const {
      data: specSections,
      refetch: refetchSpecs,
      loading
    } = useCIQQuery<{
      spec_sections: Array<{
        title: string;
        id: string;
        section_number: string;
      }>;
    }>(QUERY_GET_PROJECT_SPECS, {
      client: gqlClientForProject,
      skip: !gqlClientForProject
    });

    useImperativeHandle(ref, () => ({
      refetchSpecs
    }));

    useEffect(() => {
      if (!selectedIds || !specSections) return;
      if (setSpecSections) {
        setSpecSections(specSections?.spec_sections);
      }
      setLocalSelectedIds(
        structuredClone(selectedIds).filter(
          (id: unknown) => id != null && id !== ""
        )
      );
    }, [selectedIds, setSpecSections, specSections]);

    useEffect(() => {
      const hasInsertEvent = eventLogs.some(
        (log) =>
          log.data_source === "spec_sections" && log.change_type === "insert"
      );
      if (hasInsertEvent) {
        refetchSpecs();
      }
    }, [eventLogs, refetchSpecs]);

    const isSingleMode = mode === "single";
    const selectValue = isSingleMode
      ? localSelectedIds.find(Boolean)
      : localSelectedIds;

    return (
      <Select
        showSearch={{
          filterOption: (inputValue, option: any) =>
            option.label.toLowerCase().includes(inputValue.toLowerCase())
        }}
        mode={isSingleMode ? undefined : "multiple"}
        disabled={disabled}
        placeholder={placeholder}
        value={selectValue}
        options={specSections?.spec_sections.map((spec) => ({
          value: spec.id,
          label: spec.title
            ? `${spec.section_number} - ${spec.title}`
            : spec.section_number
        }))}
        onChange={(ids: any, op: any) => {
          if (mode === "multiple") {
            setLocalSelectedIds(ids);
            setLocalOptions(op);
          } else {
            setLocalSelectedIds(ids ? [ids] : []);
            setLocalOptions(op ? [op] : []);
            onBlur(ids ? [ids] : [], op ? [op] : []);
          }
        }}
        onBlur={() => {
          if (mode === "multiple") {
            onBlur(localSelectedIds, localOptions);
          }
        }}
        removeIcon={null}
        popupRender={popupRender}
        className={className}
        open={open}
        loading={loading}
        notFoundContent={loading ? null : "No spec sections found"}
        data-testid="select-spec-section"
        // See renderSpecSectionValue: adds a class-independent automation hook
        // on the selected value (single + multiple mode).
        labelRender={(labelProps) =>
          labelProps.label || labelProps.value
            ? renderSpecSectionValue(labelProps.label, labelProps.value)
            : undefined
        }
        allowClear={isSingleMode && Boolean(selectValue)}
      />
    );
  }
);

export default FeatureSpecSectionDropdown;
